import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Device } from "@twilio/voice-sdk";
import {
  PhoneCall, PhoneOff, Loader2, ListChecks, CheckCircle2, Upload, UserPlus,
  ArrowRightLeft, LogOut, Users, RefreshCw, Target, PhoneForwarded,
} from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const DISPOSITIONS = [
  "No Answer", "Voicemail", "Callback Requested", "Interested", "Not Interested",
  "Wrong Number", "Do Not Call", "Sale / Application Started", "Transferred to Agent",
  "Follow-Up Required", "Completed", "Other",
];

function LeadUpload({ onImported }) {
  const fileRef = useRef();
  const [analyzing, setAnalyzing] = useState(false);
  const [preview, setPreview] = useState(null); // {session_id, filename, tabs}
  const [campaigns, setCampaigns] = useState([]);
  const [campaignId, setCampaignId] = useState("");
  const [campaignName, setCampaignName] = useState("");
  const [committing, setCommitting] = useState(false);

  useEffect(() => {
    api.get("/telephony/campaigns").then(({ data }) => setCampaigns(data)).catch(() => {});
  }, []);

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAnalyzing(true);
    setPreview(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/telephony/leads/analyze", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setPreview(data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not read the file");
    } finally {
      setAnalyzing(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const commit = async () => {
    if (!campaignId && !campaignName.trim()) {
      toast.error("Choose an existing campaign or name a new one");
      return;
    }
    setCommitting(true);
    try {
      const { data } = await api.post("/telephony/leads/commit", {
        session_id: preview.session_id,
        campaign_id: campaignId,
        campaign_name: campaignName.trim(),
      });
      toast.success(`Imported ${data.inserted} lead(s) into "${data.campaign_name}"`);
      setPreview(null);
      setCampaignName("");
      setCampaignId("");
      const { data: c } = await api.get("/telephony/campaigns");
      setCampaigns(c);
      onImported?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Import failed");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="rounded-lg border-2 border-gold/30 bg-card p-5 card-elevated" data-testid="telephony-lead-upload">
      <div className="mb-3 flex items-center gap-2.5">
        <div className="grid h-8 w-8 shrink-0 place-items-center rounded-full bg-gold" style={{ color: "hsl(var(--graphite-deep))" }}>
          <Upload className="h-4 w-4" />
        </div>
        <div>
          <div className="text-sm font-bold font-heading">Upload Lead List</div>
          <div className="text-[11px] text-muted-foreground">Step 2 — bring cold prospects into the Auto Dialer queue</div>
        </div>
      </div>
      <div className="flex items-center gap-3 mb-3">
        <Button onClick={() => fileRef.current?.click()} disabled={analyzing} data-testid="telephony-upload-btn" className="brand-action">
          {analyzing ? <><Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> Analyzing…</> : <><Upload className="mr-1.5 h-4 w-4" /> Choose CSV / XLSX</>}
        </Button>
        <input ref={fileRef} type="file" accept=".csv,.xlsx" onChange={onFile} className="hidden" data-testid="telephony-upload-file" />
        <span className="text-xs text-muted-foreground">First/Last/Full Name, Phone, Alt Phone, Email, State, Lead Source, Notes, Assigned Caller, External Lead ID</span>
      </div>

      {preview && (
        <div className="space-y-3">
          {preview.tabs.map((tab) => (
            <div key={tab.name} className="rounded-md border border-border p-3 text-sm" data-testid="telephony-upload-preview">
              <div className="flex flex-wrap items-center gap-2 mb-2">
                <Badge variant="secondary">{tab.valid_count} valid</Badge>
                {tab.rejected_count > 0 && <Badge variant="outline">{tab.rejected_count} rejected</Badge>}
              </div>
              {tab.rejected_count > 0 && (
                <div className="mb-2 max-h-24 overflow-y-auto text-xs text-muted-foreground">
                  {tab.rejected_sample.slice(0, 5).map((r, i) => (
                    <div key={i}>{r.reason}</div>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="flex flex-wrap items-end gap-2">
            <div>
              <Label className="text-xs">Existing campaign</Label>
              <Select value={campaignId || "__new__"} onValueChange={(v) => setCampaignId(v === "__new__" ? "" : v)}>
                <SelectTrigger className="h-9 w-52" data-testid="telephony-upload-campaign-select"><SelectValue placeholder="New campaign" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="__new__">New campaign…</SelectItem>
                  {campaigns.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {!campaignId && (
              <div>
                <Label className="text-xs">New campaign name</Label>
                <Input className="h-9 w-52" value={campaignName} onChange={(e) => setCampaignName(e.target.value)} data-testid="telephony-upload-campaign-name" />
              </div>
            )}
            <Button onClick={commit} disabled={committing} data-testid="telephony-upload-commit">
              {committing ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
              Import into Queue
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}

function SupervisorOverview({ refreshKey }) {
  const [data, setData] = useState(null);
  const load = () => api.get("/telephony/supervisor/overview").then(({ data }) => setData(data)).catch(() => {});
  useEffect(() => { load(); }, [refreshKey]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return null;
  return (
    <div className="rounded-lg border border-border bg-card p-5" data-testid="telephony-supervisor-overview">
      <div className="overline mb-3 flex items-center justify-between">
        <span className="flex items-center gap-1.5"><Users className="h-3.5 w-3.5" /> Campaign Supervisor</span>
        <Button variant="ghost" size="sm" onClick={load}><RefreshCw className="h-3.5 w-3.5" /></Button>
      </div>
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-6 mb-4">
        <Stat label="Total Leads" value={data.total_leads} />
        <Stat label="Attempted Today" value={data.attempted_today} />
        <Stat label="Active Claims" value={data.active_claims.length} />
        <Stat label="Callbacks" value={data.callback_count} />
        <Stat label="DNC" value={data.dnc_count} />
        <Stat label="Closed" value={data.by_status?.closed || 0} />
      </div>
      {data.active_claims.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">On a call now</div>
          <div className="space-y-1 text-sm">
            {data.active_claims.map((c) => (
              <div key={c.lead_id} className="flex justify-between rounded-md bg-secondary/40 px-2.5 py-1.5">
                <span>{c.client_name}</span>
                <span className="text-muted-foreground">{c.claimed_by_name} · {c.campaign_name}</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.caller_productivity.length > 0 && (
        <div className="mb-4">
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Today's productivity</div>
          <div className="space-y-1 text-sm">
            {data.caller_productivity.map((p) => (
              <div key={p.caller} className="flex justify-between rounded-md bg-secondary/40 px-2.5 py-1.5">
                <span>{p.caller}</span>
                <span className="text-muted-foreground">{p.attempts} attempt(s)</span>
              </div>
            ))}
          </div>
        </div>
      )}
      {data.campaigns.length > 0 && (
        <div>
          <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-1.5">Campaign progress</div>
          <div className="space-y-1 text-sm">
            {data.campaigns.map((c) => (
              <div key={c.campaign_id} className="flex justify-between rounded-md bg-secondary/40 px-2.5 py-1.5">
                <span>{c.name}{!c.active && <Badge variant="outline" className="ml-2">inactive</Badge>}</span>
                <span className="text-muted-foreground">{c.closed}/{c.total} closed · {c.queued} queued · {c.dnc} DNC</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

const PARTICIPANT_STATUS_STYLE = {
  dialing: "bg-secondary text-muted-foreground",
  ringing: "bg-amber-50 text-amber-700",
  joined: "bg-emerald-50 text-emerald-700",
  failed: "bg-red-50 text-red-700",
  left: "bg-secondary text-muted-foreground",
};
const PARTICIPANT_STATUS_LABEL = {
  dialing: "Dialing", ringing: "Ringing", joined: "Joined", failed: "Failed", left: "Left",
};

function ParticipantRow({ p }) {
  const style = PARTICIPANT_STATUS_STYLE[p.status] || PARTICIPANT_STATUS_STYLE.dialing;
  const label = PARTICIPANT_STATUS_LABEL[p.status] || p.status || "Dialing";
  return (
    <div className="flex items-center justify-between rounded-md border border-border bg-card px-3 py-1.5 text-sm" data-testid={`tld-participant-${p.call_sid}`}>
      <span>{p.label || "Participant"} <span className="text-muted-foreground font-mono text-xs">{p.to}</span></span>
      <span className={`inline-flex items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-medium ${style}`}>
        {(p.status === "dialing" || p.status === "ringing") && <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-current" />}
        {label}
      </span>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded-md border border-t-2 border-t-gold border-border bg-secondary/30 p-3">
      <div className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-xl font-bold font-heading">{value}</div>
    </div>
  );
}

export default function TelephonyLeadDialer() {
  const { user } = useAuth();
  const isAdmin = ["admin", "ops_manager"].includes(user.role);
  const [queue, setQueue] = useState(null);
  const [dialing, setDialing] = useState(false);
  const [lead, setLead] = useState(null);       // current claimed lead
  const [callLogId, setCallLogId] = useState(null);
  const [calling, setCalling] = useState(false);
  const [connecting, setConnecting] = useState(false);
  const [busy, setBusy] = useState(false);
  const [disp, setDisp] = useState({ disposition: "", notes: "", callback_at: "" });
  const [addPersonOpen, setAddPersonOpen] = useState(false);
  const [addPersonPhone, setAddPersonPhone] = useState("");
  const [transferRequested, setTransferRequested] = useState(false);
  const [participants, setParticipants] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const deviceRef = useRef(null);
  const connRef = useRef(null);
  const participantsTimerRef = useRef(null);

  // Live Call Controls: poll participant states (Dialing/Ringing/Joined/Failed)
  // while a call is up, so Leave Call can be gated on an actual join -- never
  // just on having clicked Transfer.
  useEffect(() => {
    if (!calling || !callLogId) {
      setParticipants([]);
      if (participantsTimerRef.current) clearInterval(participantsTimerRef.current);
      return;
    }
    const poll = () => {
      api.get(`/voice/calls/${callLogId}/participants`).then(({ data }) => {
        setParticipants(data.participants || []);
      }).catch(() => {});
    };
    poll();
    participantsTimerRef.current = setInterval(poll, 2000);
    return () => { if (participantsTimerRef.current) clearInterval(participantsTimerRef.current); };
  }, [calling, callLogId]);

  const hasJoinedParticipant = participants.some((p) => p.status === "joined");

  const loadQueue = () => {
    api.get("/telephony/queue").then(({ data }) => {
      setQueue(data);
      if (data.current && !lead) setLead(data.current);
    }).catch(() => {});
  };
  useEffect(() => { loadQueue(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const cleanupDevice = () => {
    try { connRef.current?.disconnect(); } catch (e) {}
    try { deviceRef.current?.destroy(); } catch (e) {}
    connRef.current = null;
    deviceRef.current = null;
    setCalling(false);
    if (participantsTimerRef.current) clearInterval(participantsTimerRef.current);
  };
  useEffect(() => () => cleanupDevice(), []);

  const startBrowserCall = async (phone, logId) => {
    try {
      const { data: t } = await api.post("/voice/token");
      const device = new Device(t.token, { logLevel: 1 });
      deviceRef.current = device;
      device.on("error", () => cleanupDevice());
      const conn = await device.connect({ params: { To: phone, CallLogId: logId } });
      connRef.current = conn;
      setCalling(true);
      ["disconnect", "cancel", "reject", "error"].forEach((ev) => conn.on(ev, () => cleanupDevice()));
    } catch (err) {
      cleanupDevice();
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Call could not start");
    }
  };

  const claimNext = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/telephony/queue/claim-next");
      if (!data.lead) {
        setLead(null);
        toast.success("Queue complete — no more eligible leads right now");
        loadQueue();
        return null;
      }
      setLead(data.lead);
      setDisp({ disposition: "", notes: "", callback_at: "" });
      setTransferRequested(false);
      setAddPersonOpen(false);
      return data.lead;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not claim the next lead");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const dial = async (l) => {
    setConnecting(true);
    try {
      const { data } = await api.post(`/telephony/leads/${l.id}/dial`);
      setCallLogId(data.call_log_id);
      await startBrowserCall(l.phone, data.call_log_id);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not place the call");
    } finally {
      setConnecting(false);
    }
  };

  const startCalling = async () => {
    const l = await claimNext();
    if (l) {
      setDialing(true);
      await dial(l);
    }
  };

  const hangUp = () => cleanupDevice();

  const saveAndNext = async () => {
    if (!disp.disposition) { toast.error("Select a disposition"); return; }
    if (disp.disposition === "Callback Requested" && !disp.callback_at) {
      toast.error("Set the exact callback date/time");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/telephony/leads/${lead.id}/disposition`, disp);
      toast.success(`Logged: ${disp.disposition}`);
      setLead(null);
      setCallLogId(null);
      setRefreshKey((k) => k + 1);
      const next = await claimNext();
      if (next) {
        await dial(next);
      } else {
        setDialing(false);
      }
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not save disposition");
    } finally {
      setBusy(false);
    }
  };

  const stopDialing = () => {
    cleanupDevice();
    setDialing(false);
    setLead(null);
    setCallLogId(null);
    loadQueue();
  };

  const addPerson = async (transfer) => {
    const digits = addPersonPhone.replace(/\D/g, "");
    if (digits.length < 10) { toast.error("Enter a valid phone number"); return; }
    setBusy(true);
    try {
      const path = transfer ? "transfer" : "add-person";
      await api.post(`/voice/calls/${callLogId}/${path}`, { to: addPersonPhone });
      toast.success(transfer ? "Transferring — connecting the new participant" : "Adding participant to the call");
      setAddPersonOpen(false);
      setAddPersonPhone("");
      if (transfer) setTransferRequested(true);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not connect the new participant");
    } finally {
      setBusy(false);
    }
  };

  const leaveCall = async () => {
    setBusy(true);
    try {
      await api.post(`/voice/calls/${callLogId}/leave`);
      toast.success("You've left the call — the client and new participant remain connected");
      cleanupDevice();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not leave the call");
    } finally {
      setBusy(false);
    }
  };

  const awaitingDisposition = lead && dialing && !calling && !connecting;

  return (
    <div className="space-y-4">
      <LeadUpload onImported={loadQueue} />

      <div className="rounded-lg border border-border bg-card p-5" data-testid="telephony-lead-dialer">
        <div className="overline mb-3 flex items-center gap-1.5"><ListChecks className="h-3.5 w-3.5" /> Auto Dialer Queue</div>

        {!dialing ? (
          <div className="space-y-4 max-w-lg">
            <div className="flex items-center gap-4 rounded-lg border-2 border-gold/40 bg-gold/5 p-4">
              <div className="grid h-11 w-11 shrink-0 place-items-center rounded-full bg-gold" style={{ color: "hsl(var(--graphite-deep))" }}>
                <Target className="h-5 w-5" />
              </div>
              <div className="flex-1">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Ready to Call</div>
                <div className="text-3xl font-bold font-heading" data-testid="tld-eligible-count">{queue?.eligible_count ?? 0}</div>
              </div>
              <Button variant="ghost" size="sm" onClick={loadQueue}><RefreshCw className="mr-1.5 h-3.5 w-3.5" /> Refresh</Button>
            </div>
            {!queue || queue.eligible_count === 0 ? (
              <p className="text-sm text-muted-foreground">No callable leads in your queue right now. Upload a list above, or check back once a callback is due.</p>
            ) : (
              <Button onClick={startCalling} disabled={busy} data-testid="tld-start" className="brand-action w-full h-12 text-base font-semibold">
                {busy ? <Loader2 className="mr-2 h-5 w-5 animate-spin" /> : <PhoneCall className="mr-2 h-5 w-5" />}
                Start Calling
              </Button>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {lead && (
              <div className="rounded-md border border-border bg-background p-4 space-y-1.5" data-testid="tld-current-lead">
                <div className="font-medium">{lead.client_name}</div>
                <div className="text-sm text-muted-foreground font-mono">{lead.phone}</div>
                <div className="text-xs text-muted-foreground flex flex-wrap gap-x-3">
                  {lead.state && <span>State: {lead.state}</span>}
                  {lead.lead_source && <span>Source: {lead.lead_source}</span>}
                  {lead.campaign_name && <span>Campaign: {lead.campaign_name}</span>}
                  <span>Attempt #{(lead.attempt_count || 0) + 1}</span>
                </div>
                {lead.notes && <p className="text-xs text-muted-foreground pt-1">{lead.notes}</p>}
              </div>
            )}

            {calling && (
              <div className="space-y-3 rounded-lg border border-border bg-secondary/10 p-4" data-testid="tld-live-call-controls">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <div className="overline flex items-center gap-1.5"><PhoneForwarded className="h-3.5 w-3.5" /> Live Call Controls</div>
                  <div className="inline-flex items-center gap-2 rounded-md bg-emerald-50 px-3 py-1.5 text-sm font-medium text-emerald-700" data-testid="tld-call-in-progress">
                    <span className="h-2 w-2 animate-pulse rounded-full bg-emerald-500" /> Call in progress
                  </div>
                </div>

                {participants.length > 0 && (
                  <div className="space-y-1.5" data-testid="tld-participants">
                    {participants.map((p) => <ParticipantRow key={p.call_sid} p={p} />)}
                  </div>
                )}

                <div className="flex flex-wrap items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setAddPersonOpen((v) => !v)} data-testid="tld-add-person-toggle">
                    <UserPlus className="mr-1.5 h-4 w-4" /> Add Person
                  </Button>
                  {transferRequested && (
                    <Button
                      variant="outline" size="sm" onClick={leaveCall}
                      disabled={busy || !hasJoinedParticipant}
                      title={hasJoinedParticipant ? "" : "Waiting for the transfer target to join before you can leave"}
                      data-testid="tld-leave-call"
                    >
                      <LogOut className="mr-1.5 h-4 w-4" /> Leave Call
                    </Button>
                  )}
                  <Button variant="destructive" size="sm" onClick={hangUp} data-testid="tld-hangup">
                    <PhoneOff className="mr-1.5 h-4 w-4" /> Hang Up
                  </Button>
                </div>
                {transferRequested && !hasJoinedParticipant && (
                  <p className="text-xs text-muted-foreground">Waiting for the transfer target to join — Leave Call unlocks once they connect.</p>
                )}
                {addPersonOpen && (
                  <div className="flex flex-wrap items-end gap-2 rounded-md border border-border bg-card p-3" data-testid="tld-add-person-form">
                    <div>
                      <Label className="text-xs">Phone number to add</Label>
                      <Input className="h-9 w-44" value={addPersonPhone} onChange={(e) => setAddPersonPhone(e.target.value)}
                        placeholder="(305) 555-1212" data-testid="tld-add-person-phone" />
                    </div>
                    <Button size="sm" onClick={() => addPerson(false)} disabled={busy} data-testid="tld-add-person-confirm">
                      <UserPlus className="mr-1.5 h-4 w-4" /> Add to Call
                    </Button>
                    <Button size="sm" variant="outline" onClick={() => addPerson(true)} disabled={busy} data-testid="tld-transfer-confirm">
                      <ArrowRightLeft className="mr-1.5 h-4 w-4" /> Transfer
                    </Button>
                  </div>
                )}
              </div>
            )}

            {awaitingDisposition && (
              <div className="max-w-lg space-y-3" data-testid="tld-disposition-form">
                <p className="text-sm font-medium">Wrap up the call — disposition is required.</p>
                <Select value={disp.disposition} onValueChange={(v) => setDisp({ ...disp, disposition: v })}>
                  <SelectTrigger data-testid="tld-disp-select"><SelectValue placeholder="Outcome" /></SelectTrigger>
                  <SelectContent>{DISPOSITIONS.map((d) => <SelectItem key={d} value={d}>{d}</SelectItem>)}</SelectContent>
                </Select>
                {disp.disposition === "Callback Requested" && (
                  <Input type="datetime-local" value={disp.callback_at}
                    onChange={(e) => setDisp({ ...disp, callback_at: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                    data-testid="tld-disp-callback-at" />
                )}
                <Textarea placeholder="Notes (optional)" value={disp.notes} onChange={(e) => setDisp({ ...disp, notes: e.target.value })} />
                <Button onClick={saveAndNext} disabled={busy} data-testid="tld-save-next">
                  {busy ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1.5 h-4 w-4" />}
                  Save &amp; Next
                </Button>
              </div>
            )}

            <div className="flex flex-wrap gap-2 border-t border-border pt-3">
              <Button variant="ghost" onClick={stopDialing} disabled={busy} data-testid="tld-stop">
                Stop Calling
              </Button>
            </div>
          </div>
        )}
      </div>

      {isAdmin && <SupervisorOverview refreshKey={refreshKey} />}
    </div>
  );
}
